// ESC/POS Commands
const ESC = 0x1b;

const CMD_INIT = [ESC, 0x40];
const CMD_ALIGN_LEFT = [ESC, 0x61, 0x00];
const CMD_ALIGN_CENTER = [ESC, 0x61, 0x01];
const CMD_FONT_COMPRESSED = [ESC, 0x4d, 0x01]; // Select Font B (compressed)
const CMD_FONT_NORMAL = [ESC, 0x4d, 0x00]; // Select Font A (normal)
const CMD_FEED_CUT = [ESC, 0x64, 0x05]; // Feed 5 lines

/**
 * Packs string into raw ASCII bytes
 */
function stringToBytes(str: string): number[] {
  const sanitized = str.replace(/₹/g, 'Rs');
  const bytes: number[] = [];
  for (let i = 0; i < sanitized.length; i++) {
    const code = sanitized.charCodeAt(i);
    if (code < 128) {
      bytes.push(code);
    } else {
      bytes.push(0x3f);
    }
  }
  return bytes;
}

/**
 * Pads or truncates a string to exact character length
 */
function padString(
  str: string,
  length: number,
  align: 'left' | 'right' | 'center' = 'left',
): string {
  const sanitized = str.replace(/₹/g, 'Rs');
  if (sanitized.length >= length) {
    return sanitized.substring(0, length);
  }
  const spaces = ' '.repeat(length - sanitized.length);
  if (align === 'right') {
    return spaces + sanitized;
  }
  if (align === 'center') {
    const half = Math.floor(spaces.length / 2);
    return spaces.slice(0, half) + sanitized + spaces.slice(half);
  }
  return sanitized + spaces;
}

/**
 * Generates structured 2-column row, e.g. "Left text               Right"
 */
function formatTwoColumns(left: string, right: string, width = 32): string {
  const leftLen = width - right.length - 1;
  const leftTruncated = padString(left, Math.max(1, leftLen), 'left');
  return leftTruncated + ' ' + right;
}

export function compileWirelessReceipt(invoice: any, tenantProfile: any): Uint8Array {
  const bytes: number[] = [];

  // 1. Initialize & setup
  bytes.push(...CMD_INIT);
  bytes.push(...CMD_FONT_COMPRESSED); // 32 col width styling font

  // 2. Header
  bytes.push(...CMD_ALIGN_CENTER);
  const shopName = tenantProfile?.company_name || 'TEXTILE STORE';
  bytes.push(...stringToBytes(padString(shopName, 32, 'center') + '\n'));

  if (tenantProfile?.company_address) {
    const addressLines = tenantProfile.company_address
      .split('\n')
      .map((line: string) => line.trim())
      .filter(Boolean);
    addressLines.forEach((line: string) => {
      bytes.push(...stringToBytes(padString(line, 32, 'center') + '\n'));
    });
  }

  if (tenantProfile?.company_mobile) {
    bytes.push(
      ...stringToBytes(padString(`Tel: +91 ${tenantProfile.company_mobile}`, 32, 'center') + '\n'),
    );
  }

  // Inject GSTIN if tenant is eligible
  const isGst = !!tenantProfile?.is_gst_eligible;
  if (isGst && tenantProfile?.tenant_gstin) {
    bytes.push(
      ...stringToBytes(padString(`GSTIN: ${tenantProfile.tenant_gstin}`, 32, 'center') + '\n'),
    );
  }

  bytes.push(...CMD_ALIGN_LEFT);
  bytes.push(...stringToBytes('--------------------------------\n'));

  // 3. Metadata
  bytes.push(...stringToBytes(`INV: ${invoice.id || invoice.invoiceNumber || 'DRAFT'}\n`));
  bytes.push(
    ...stringToBytes(`DATE: ${new Date(invoice.date || invoice.createdAt).toLocaleString()}\n`),
  );
  bytes.push(...stringToBytes(`CUST: ${invoice.customerName || 'Walk-in'}\n`));
  if (invoice.customerPhone && invoice.customerPhone !== 'N/A') {
    bytes.push(...stringToBytes(`MOB: ${invoice.customerPhone}\n`));
  }
  if (isGst && invoice.customerGstin) {
    bytes.push(...stringToBytes(`CUST GST: ${invoice.customerGstin}\n`));
  }

  bytes.push(...stringToBytes('--------------------------------\n'));

  // 4. Cart Items Columns Header
  bytes.push(...stringToBytes('ITEM             QTY  RATE   TOTAL\n'));
  bytes.push(...stringToBytes('--------------------------------\n'));

  // Items
  const items = invoice.items || [];
  items.forEach((item: any) => {
    const finalRate = item.price * (1 - (item.discount || 0) / 100);
    const lineTotalVal = finalRate * item.quantity;

    const nameStr = padString(item.name, 16, 'left');
    const qtyStr = padString(item.quantity.toString(), 3, 'right');
    const rateStr = padString(finalRate.toFixed(0), 6, 'right');
    const totalStr = padString(lineTotalVal.toFixed(0), 7, 'right');

    bytes.push(...stringToBytes(`${nameStr} ${qtyStr} ${rateStr} ${totalStr}\n`));

    const mrpVal = item.mrp || item.price || 0;
    const labelDiscPct = item.discountPercentage || item.discount_percentage || 0;
    bytes.push(...stringToBytes(padString(`  MRP: Rs.${mrpVal.toFixed(0)}`, 32, 'left') + '\n'));
    if (labelDiscPct > 0) {
      bytes.push(...stringToBytes(padString(`  [${labelDiscPct.toFixed(0)}% Disc]`, 32, 'left') + '\n'));
    }
    if (item.discount > 0) {
      bytes.push(...stringToBytes(padString(`  [${item.discount}% Extra Disc]`, 32, 'left') + '\n'));
    }
  });

  bytes.push(...stringToBytes('--------------------------------\n'));

  // 5. Financial Summary calculations
  const subtotal = invoice.subtotal || 0;
  const discAmt = invoice.overallDiscountAmount || 0;
  const netSub = subtotal - discAmt;
  const totalGst =
    invoice.totalGst || invoice.cgstTotal + invoice.sgstTotal + invoice.igstTotal || 0;
  const grandTotal = invoice.grandTotal || netSub + totalGst;

  bytes.push(
    ...stringToBytes(formatTwoColumns('Gross Subtotal:', `Rs.${subtotal.toFixed(2)}`) + '\n'),
  );

  if (discAmt > 0) {
    bytes.push(
      ...stringToBytes(
        formatTwoColumns(
          `Discount (${invoice.overallDiscount || 0}%):`,
          `-Rs.${discAmt.toFixed(2)}`,
        ) + '\n',
      ),
    );
    bytes.push(
      ...stringToBytes(formatTwoColumns('Net Taxable:', `Rs.${netSub.toFixed(2)}`) + '\n'),
    );
  }

  if (isGst && totalGst > 0) {
    bytes.push(
      ...stringToBytes(formatTwoColumns('Total Tax (GST):', `Rs.${totalGst.toFixed(2)}`) + '\n'),
    );

    if (invoice.cgstTotal > 0 || invoice.sgstTotal > 0) {
      const cgstVal = invoice.cgstTotal || totalGst / 2;
      const sgstVal = invoice.sgstTotal || totalGst / 2;
      bytes.push(
        ...stringToBytes(formatTwoColumns('  CGST (Central):', `Rs.${cgstVal.toFixed(2)}`) + '\n'),
      );
      bytes.push(
        ...stringToBytes(formatTwoColumns('  SGST (State):', `Rs.${sgstVal.toFixed(2)}`) + '\n'),
      );
    } else if (invoice.igstTotal > 0) {
      bytes.push(
        ...stringToBytes(
          formatTwoColumns('  IGST (Integrated):', `Rs.${invoice.igstTotal.toFixed(2)}`) + '\n',
        ),
      );
    }
  }

  bytes.push(...stringToBytes('================================\n'));
  bytes.push(...CMD_FONT_NORMAL);
  bytes.push(
    ...stringToBytes(formatTwoColumns('GRAND TOTAL:', `Rs.${grandTotal.toFixed(2)}`, 32) + '\n'),
  );
  bytes.push(...CMD_FONT_COMPRESSED);
  bytes.push(...stringToBytes('================================\n'));

  if (invoice.loyaltyPointsEarned > 0) {
    bytes.push(...CMD_ALIGN_CENTER);
    bytes.push(...stringToBytes(`POINTS EARNED: +${invoice.loyaltyPointsEarned} PTS\n`));
  }

  // Footer message
  bytes.push(...CMD_ALIGN_CENTER);

  // Calculate total MRP savings
  let totalMrpSavings = 0;
  items.forEach((item: any) => {
    const mrp = item.mrp || item.price || 0;
    const price = item.price || 0;
    const qty = item.quantity || 0;
    if (mrp > price) {
      totalMrpSavings += (mrp - price) * qty;
    }
  });

  if (totalMrpSavings > 0) {
    bytes.push(...stringToBytes(`Total MRP Savings: Rs. ${totalMrpSavings.toFixed(2)}\n`));
  }

  bytes.push(...stringToBytes('Thank you for shopping!\n'));
  bytes.push(...stringToBytes('Please visit again.\n'));
  bytes.push(...CMD_ALIGN_LEFT);

  bytes.push(...CMD_FEED_CUT);

  return new Uint8Array(bytes);
}
